import * as Sentry from '@sentry/react';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  FormOrQuizSelection,
  FormOrQuizSelector,
} from '../components/FormFields/FormOrQuizSelector';
import { SnackbarContext } from '../contexts/SnackbarContext';

interface Props {
  /** Current form ID (legacy) */
  currentFormId?: string;
  open: boolean;
  onClose: () => void;
  onSave: (selection: {
    formId?: string;
    quizId?: string;
  }) => Promise<void> | void;
}

export default function ReplaceFormModal({
  currentFormId,
  open,
  onClose,
  onSave,
}: Props) {
  const { show } = useContext(SnackbarContext);

  const [selection, setSelection] = useState<FormOrQuizSelection>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    // Initialize with current selection
    if (currentFormId) {
      setSelection({ type: 'form', id: currentFormId });
    } else {
      setSelection(null);
    }
  }, [open, currentFormId]);

  const currentSelectionKey = currentFormId ? `form:${currentFormId}` : null;
  const newSelectionKey = selection
    ? `${selection.type}:${selection.id}`
    : null;

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    if (!selection) {
      setError(new Error('Please select a form or quiz'));
      show('Please select a form or quiz');
      return;
    }

    if (currentSelectionKey === newSelectionKey) {
      setError(new Error('Please select a different form or quiz'));
      show('Please select a different form or quiz');
      return;
    }

    setIsSaving(true);
    setError(undefined);

    try {
      const result =
        selection.type === 'form'
          ? { formId: selection.id, quizId: undefined }
          : { formId: undefined, quizId: selection.id };
      const res = onSave(result);
      if (res && 'then' in res) {
        await res;
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
      show(e.message || 'Failed to replace form');
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    selection,
    currentSelectionKey,
    newSelectionKey,
    onSave,
    onClose,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSaving,
        onClick: () => onClose(),
      },
      {
        title: 'Replace',
        type: 'primary',
        loading: isSaving,
        onClick: handleSave,
      },
    ],
    [isSaving, onClose, handleSave],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="md"
      title="Replace Form or Quiz"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Select a new form or quiz to replace the current one in this section.
        </p>

        <FormOrQuizSelector
          title="Select Form or Quiz"
          formId={selection?.id}
          onChange={(newSelection) => {
            setSelection(newSelection);
            setError(undefined);
          }}
        />

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error.message}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
